import { observable, makeObservable, action, toJS } from "mobx";
import {
    LLMExecutorStep,
    LLMExecutorStepType,
    LLMExecutorStepOutputActionType,
    LLMExecutorStepExecuteStepConfiguration,
} from "models/LLMs";
import { LLMClassifierImplementationConfigurationLogicChain } from "models/LLMs/LLMClassifierImplementation";
import { deepCamelCase, deepPascalCase } from "../utils";
import {
    ILLMClassifierConfigurationBuilder,
    LLMClassifierConfigurationBuilder,
} from "./LLMClassifierConfigurationBuilder";

export class LLMClassifierLogicChainBuilder
    extends LLMClassifierConfigurationBuilder
    implements ILLMClassifierConfigurationBuilder
{
    @observable readonly steps: LLMExecutorStep[];

    constructor(
        configuration?: LLMClassifierImplementationConfigurationLogicChain,
    ) {
        super(configuration);
        makeObservable(this);
        this.steps = observable.array();

        if (configuration) {
            const camelized = deepCamelCase(configuration, [
                "inputVariables",
                "structuredOutputJsonSchema",
            ]);
            this.steps = observable.array(
                camelized.steps.map((step) => new LLMExecutorStep(step)),
            );
        } else {
            const defaultStep = this.addStep();
            defaultStep.configuration.addAction();
        }
    }

    public getNextStepName() {
        return `Step ${this.steps.length + 1}`;
    }

    @action
    public addStep(
        type: LLMExecutorStepType = LLMExecutorStepType.ExecutePrompt,
    ): LLMExecutorStep {
        const step = new LLMExecutorStep(type);

        if (this.steps.length === 0) step.setIsEntryPoint(true);

        step.setName(this.getNextStepName());
        this.steps.push(step);
        return step;
    }

    @action
    public removeStep(step: LLMExecutorStep) {
        const index = this.steps.indexOf(step);
        if (index < 0) return false;

        const stepId = step.id;

        // need to clear out step ids on any thenActionConfigurations
        for (const otherStep of this.steps) {
            if (otherStep.id === stepId) continue;

            for (const action of otherStep.configuration.actions) {
                if (
                    action.thenActionType !==
                    LLMExecutorStepOutputActionType.ExecuteStep
                )
                    continue;

                const configuration =
                    action.thenActionConfiguration as LLMExecutorStepExecuteStepConfiguration;

                if (configuration.stepId === stepId) configuration.stepId = "";
            }
        }

        this.steps.splice(index, 1);
        return true;
    }

    public getStepbyId(stepId: string): LLMExecutorStep | undefined {
        return this.steps.find((step) => step.id === stepId);
    }

    isTestable(): boolean {
        return this.steps.length > 0;
    }

    isSavable(): boolean {
        return (
            this.steps.length > 0 &&
            this.steps.every(
                (step) =>
                    !!step.name &&
                    !!step.configuration.prompt &&
                    // at least 1 action to take
                    step.configuration.actions.length > 0,
            ) &&
            // at least 1 entry point
            this.steps.some((step) => step.isEntryPoint)
        );
    }

    getConfiguration(): LLMClassifierImplementationConfigurationLogicChain {
        return {
            ...super.getConfiguration(),
            steps: deepPascalCase(this.getSerializableSteps(), [
                "InputVariables",
                "StructuredOutputJsonSchema",
            ]),
        };
    }

    private getSerializableSteps() {
        return toJS(this.steps).map((step) => {
            if (!step.modelOptions?.thinkingEffortLevel) return step;

            return {
                ...step,
                modelOptions: {
                    thinkingEffortLevel: step.modelOptions.thinkingEffortLevel,
                },
            };
        });
    }
}
